import React from "react";
import { Button } from "@mui/material";

const baseSx = {
  minHeight: 40,
  borderRadius: "8px",
  px: 2.25,
  textTransform: "none",
  fontWeight: 650,
  fontSize: 14,
  lineHeight: "20px",
  boxShadow: "none",
  transition: "background-color .16s ease, border-color .16s ease, box-shadow .16s ease, color .16s ease, transform .08s ease",
  "&:hover": {
    boxShadow: "none",
  },
  "&:active": {
    transform: "translateY(1px)",
  },
  "&.Mui-focusVisible": {
    boxShadow: "0 0 0 4px rgba(11, 92, 171, .18)",
  },
  "&.Mui-disabled": {
    color: "#c7cbd1",
    backgroundColor: "#fafafa",
    borderColor: "#f2f3f5",
  },
};

const sizeSx = {
  small: { minHeight: 34, px: 1.75, fontSize: 13 },
  medium: {},
  large: { minHeight: 46, px: 2.75, fontSize: 15 },
};

const variantSx = {
  contained: {
    color: "#fff",
    backgroundColor: "var(--sub-primary, #0b5cab)",
    border: "1px solid var(--sub-primary, #0b5cab)",
    "&:hover": {
      backgroundColor: "var(--sub-primary-dark, #08447f)",
      borderColor: "var(--sub-primary-dark, #08447f)",
    },
    "&:active": {
      backgroundColor: "var(--sub-primary-dark, #08447f)",
      borderColor: "var(--sub-primary-dark, #08447f)",
    },
  },
  outlined: {
    color: "var(--sub-primary, #0b5cab)",
    backgroundColor: "#fff",
    border: "1px solid color-mix(in srgb, var(--sub-primary, #0b5cab) 28%, white)",
    "&:hover": {
      backgroundColor: "var(--sub-primary-soft, #eaf3fb)",
      borderColor: "color-mix(in srgb, var(--sub-primary, #0b5cab) 45%, white)",
    },
    "&:active": {
      backgroundColor: "color-mix(in srgb, var(--sub-primary-soft, #eaf3fb) 72%, var(--sub-primary, #0b5cab))",
      borderColor: "color-mix(in srgb, var(--sub-primary, #0b5cab) 55%, white)",
    },
  },
  text: {
    color: "var(--sub-primary, #0b5cab)",
    backgroundColor: "transparent",
    border: "1px solid transparent",
    "&:hover": {
      backgroundColor: "var(--sub-primary-soft, #eaf3fb)",
    },
    "&:active": {
      backgroundColor: "color-mix(in srgb, var(--sub-primary-soft, #eaf3fb) 72%, var(--sub-primary, #0b5cab))",
    },
  },
  soft: {
    color: "var(--sub-primary, #0b5cab)",
    backgroundColor: "var(--sub-primary-soft, #eaf3fb)",
    border: "1px solid transparent",
    "&:hover": {
      backgroundColor: "color-mix(in srgb, var(--sub-primary-soft, #eaf3fb) 85%, var(--sub-primary, #0b5cab))",
    },
    "&:active": {
      backgroundColor: "color-mix(in srgb, var(--sub-primary-soft, #eaf3fb) 72%, var(--sub-primary, #0b5cab))",
    },
  },
  neutral: {
    color: "var(--sub-text, #17212b)",
    backgroundColor: "#fff",
    border: "1px solid var(--sub-border, #dfe6ed)",
    "&:hover": {
      backgroundColor: "#f4f7fa",
      borderColor: "color-mix(in srgb, var(--sub-border, #dfe6ed) 70%, var(--sub-primary, #0b5cab))",
    },
    "&:active": {
      backgroundColor: "#eaf0f6",
      borderColor: "color-mix(in srgb, var(--sub-border, #dfe6ed) 55%, var(--sub-primary, #0b5cab))",
    },
  },
};

const colorVariantSx = {
  error: {
    contained: {
      color: "#fff",
      backgroundColor: "#d32f2f",
      border: "1px solid #d32f2f",
      "&:hover": {
        backgroundColor: "#b71c1c",
        borderColor: "#b71c1c",
      },
      "&:active": {
        backgroundColor: "#b71c1c",
        borderColor: "#b71c1c",
      },
      "&.Mui-focusVisible": {
        boxShadow: "0 0 0 4px rgba(211, 47, 47, .18)",
      },
    },
    outlined: {
      color: "#d32f2f",
      backgroundColor: "#fff",
      border: "1px solid rgba(211, 47, 47, .38)",
      "&:hover": {
        backgroundColor: "rgba(211, 47, 47, .08)",
        borderColor: "rgba(211, 47, 47, .58)",
      },
      "&:active": {
        backgroundColor: "rgba(211, 47, 47, .14)",
        borderColor: "#d32f2f",
      },
      "&.Mui-focusVisible": {
        boxShadow: "0 0 0 4px rgba(211, 47, 47, .18)",
      },
    },
    text: {
      color: "#d32f2f",
      backgroundColor: "transparent",
      border: "1px solid transparent",
      "&:hover": {
        backgroundColor: "rgba(211, 47, 47, .08)",
      },
      "&:active": {
        backgroundColor: "rgba(211, 47, 47, .14)",
      },
      "&.Mui-focusVisible": {
        boxShadow: "0 0 0 4px rgba(211, 47, 47, .18)",
      },
    },
    soft: {
      color: "#b71c1c",
      backgroundColor: "rgba(211, 47, 47, .1)",
      border: "1px solid transparent",
      "&:hover": {
        backgroundColor: "rgba(211, 47, 47, .16)",
      },
      "&:active": {
        backgroundColor: "rgba(211, 47, 47, .22)",
      },
      "&.Mui-focusVisible": {
        boxShadow: "0 0 0 4px rgba(211, 47, 47, .18)",
      },
    },
  },
};

export default function AppButton({
  variant = "text",
  color,
  size = "small",
  sx,
  disableElevation = true,
  useSemanticColor = true,
  ...props
}) {
  const resolvedVariant = color === "inherit" ? "neutral" : variant;
  const muiVariant = resolvedVariant === "contained" ? "contained" : "outlined";

  return (
    <Button
      {...props}
      variant={muiVariant}
      color={color === "inherit" ? "inherit" : color}
      size={size}
      disableElevation={disableElevation}
      sx={{
        ...baseSx,
        ...(sizeSx[size] || {}),
        ...(variantSx[resolvedVariant] || variantSx.text),
        ...(useSemanticColor ? colorVariantSx[color]?.[resolvedVariant] || {} : {}),
        ...sx,
      }}
    />
  );
}
